/**
 * NoteDetail — a matter note, in the shared detail container.
 *
 * Replaces the `editMatterData` modal the Notes tab used to open. Same three
 * fields and the same endpoints; the difference is that it lives in a docked
 * pane beside the list, so editing a note no longer covers the list you are
 * editing it from, and moving between notes is one click instead of
 * close-then-reopen.
 *
 * Endpoints, unchanged:
 *   create  `addNoteToMatter(matterUuid, { label, value, type })`
 *   update  `updateNoteInMatter(matterUuid, { label, value, type, oldLabel })`
 *   delete  `deleteNoteFromMatter(matterUuid, label)`
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiTrashLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../../store';
import {
  addNoteToMatter,
  deleteNoteFromMatter,
  updateNoteInMatter,
} from '../../../../store/actions/Matters/MatterActions';
import { hideModal, showModal } from '../../../../store/actions/modalActions';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import Button from '../../../shared/TailwindComponents/Button';
import Input from '../../../shared/TailwindComponents/Input';
import Textarea from '../../../shared/TailwindComponents/Textarea';
import Dropdown from '../../../shared/TailwindComponents/Dropdown';
import Spinner from '../../../shared/TailwindComponents/Spinner';

export type NoteType = 'short_text' | 'long_text' | 'number' | 'date' | 'boolean';

export interface DetailNote {
  label: string;
  value: string;
  type?: NoteType;
}

interface Props {
  matterUuid: string;
  /** Null in create mode. */
  note: DetailNote | null;
  canEdit: boolean;
  onSaved: () => void;
  onClose: () => void;
}

const TYPE_OPTIONS: Array<{ value: NoteType; label: string }> = [
  { value: 'short_text', label: 'Text' },
  { value: 'long_text', label: 'Note' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
];

const NoteDetail: React.FC<Props> = ({ matterUuid, note, canEdit, onSaved, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [type, setType] = useState<NoteType>('short_text');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /*
    Reset the form when the SELECTED NOTE changes — keyed on its label, which
    is this record's identity. Keying on the object would reset the form on
    every background refresh and throw away whatever was being typed.
  */
  useEffect(() => {
    setLabel(note?.label || '');
    setValue(note?.value ?? '');
    setType(note?.type || 'short_text');
    setError('');
  }, [note?.label]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCreate = !note;

  const save = async () => {
    if (!label.trim()) {
      setError(t('A label is required'));
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await dispatch(addNoteToMatter(matterUuid, { label: label.trim(), value, type }));
      } else {
        await dispatch(updateNoteInMatter(matterUuid, {
          label: label.trim(),
          value,
          type,
          // The label IS the key, so a rename has to tell the API which
          // record it is renaming.
          oldLabel: note!.label,
        }));
      }
      onSaved();
      if (isCreate) onClose();
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', message: t('Failed to save note'), duration: 3000,
      }));
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    if (!note) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Delete note'),
      message: t('Delete this note?'),
      subText: t('It will be permanently removed. This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      onConfirm: async () => {
        dispatch(hideModal());
        await dispatch(deleteNoteFromMatter(matterUuid, note.label));
        onSaved();
        onClose();
      },
      onClose: () => dispatch(hideModal()),
    } as any));
  };

  return (
    <div className="p-4 space-y-4">
      <Input
        type="text"
        label={t('Label')}
        placeholder={t('e.g. Contract value')}
        value={label}
        onChange={(e) => { setLabel(e.target.value); setError(''); }}
        error={error}
        disabled={!canEdit}
        className="w-full"
      />

      <div>
        <label className="block text-sm font-medium text-fg-body mb-1">{t('Type')}</label>
        <Dropdown
          options={TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={type}
          onChange={(next) => setType((Array.isArray(next) ? next[0] : next) as NoteType)}
          className="w-full"
        />
      </div>

      {type === 'long_text' ? (
        <Textarea
          id="note-value"
          label={t('Value')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit}
          rows={6}
          className="w-full"
        />
      ) : (
        <Input
          type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
          label={t('Value')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit}
          className="w-full"
        />
      )}

      {canEdit && (
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-hairline-soft">
          {!isCreate ? (
            <Button variant="destructive" size="small" onClick={remove} disabled={saving}>
              {t('Delete')}
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="neutral" size="small" onClick={onClose} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={save}
              disabled={saving}
              icon={saving ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
            >
              {saving ? t('Saving...') : isCreate ? t('Create') : t('Save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteDetail;
